import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FACTURATION_SECTION_ARCHIVE_ROUTES } from '../../../pages/facturation/config/facturationSectionArchivesConfig';

const HIDE_PATHS = new Set(['/settings', '/login', '/setup', '/dashboard', '/']);

const SKIP_HOIST_PATHS = new Set([
  '/archives',
  '/inventaire/archives',
  '/logistique/archives',
  ...FACTURATION_SECTION_ARCHIVE_ROUTES,
]);

const FILTERS_SLOT_CLASS =
  'page-title-date-row__filters qawaem-title-filters-slot flex flex-wrap items-center gap-2 flex-1 min-w-0 justify-start';

function findControlStrip(container) {
  const selectors = [
    '.facturation-list-controls-strip',
    '.glass-panel > .p-4.border-b',
    '.glass-table-shell > .p-4.border-b',
  ];

  for (const sel of selectors) {
    const el = container.querySelector(sel);
    if (el?.querySelector('input[type="text"], .facturation-list-toolbar, .facturation-view-mode-toggle, .qawaem-view-toggle')) {
      return el;
    }
  }

  const candidates = container.querySelectorAll('.p-4.border-b');
  for (const el of candidates) {
    if (el.closest('.fixed.inset-0')) continue;
    if (el.classList.contains('qawaem-toolbar-source--hoisted')) continue;
    if (el.querySelector('input[type="text"][placeholder]')) return el;
  }

  return null;
}

function safeInsertBefore(parent, node, before) {
  if (!parent?.isConnected || !node) return false;
  try {
    if (before && before.parentElement === parent) {
      parent.insertBefore(node, before);
    } else {
      parent.appendChild(node);
    }
    return true;
  } catch (_) {
    return false;
  }
}

function rememberHoistOrigin(node) {
  if (!node || node.dataset.qawaemHoistOrigin === '1') return;
  node.dataset.qawaemHoistOrigin = '1';
  node._qawaemHoistOriginalParent = node.parentElement;
  node._qawaemHoistOriginalNext = node.nextSibling;
}

/** Undo title-row DOM wrapping so React reconciliation matches the virtual tree again. */
function restoreTitleRowDom(container) {
  if (!container) return;

  container.querySelectorAll('.list-toolbar-mobile-layout > div:first-child').forEach((titleCol) => {
    const row = titleCol.querySelector(':scope > .page-title-date-row');
    if (!row) return;

    const h1 =
      row.querySelector(':scope > h1') ||
      row.querySelector('.page-title-date-row__title h1');
    if (h1 && h1.parentElement !== titleCol) {
      safeInsertBefore(titleCol, h1, row);
    }

    row.querySelector('.page-title-date-row__title:empty')?.remove();

    const filtersSlot = row.querySelector('.qawaem-title-filters-slot, .page-title-date-row__filters');
    if (filtersSlot && !filtersSlot.querySelector('.archives-toolbar-filters')) {
      const onlyHoisted = [...filtersSlot.children].every(
        (child) =>
          child.classList?.contains('qawaem-hoisted-control') ||
          child.classList?.contains('qawaem-toolbar-source--hoisted'),
      );
      if (onlyHoisted || filtersSlot.childElementCount === 0) {
        filtersSlot.replaceChildren();
      }
    }

    if (row.parentElement === titleCol) {
      row.remove();
    }
  });
}

function restoreHoistedNodes(container) {
  if (!container) return;

  container.querySelectorAll('.qawaem-hoisted-control').forEach((node) => {
    const parent = node._qawaemHoistOriginalParent;
    const next = node._qawaemHoistOriginalNext;
    if (parent?.isConnected) {
      safeInsertBefore(parent, node, next?.parentElement === parent ? next : null);
    }
    node.classList.remove('qawaem-hoisted-control');
    delete node.dataset.qawaemHoistOrigin;
    delete node._qawaemHoistOriginalParent;
    delete node._qawaemHoistOriginalNext;
  });

  container.querySelectorAll('.qawaem-toolbar-source--hoisted').forEach((strip) => {
    strip.classList.remove('qawaem-toolbar-source--hoisted');
    delete strip.dataset.qawaemHoistSource;
  });
}

function collectHoistNodes(strip) {
  const nodes = [];
  const seen = new Set();

  const push = (node) => {
    if (!node || seen.has(node)) return;
    seen.add(node);
    nodes.push(node);
  };

  const row =
    strip.querySelector('.facturation-list-controls-row') ||
    strip.querySelector('.flex.flex-col.lg\\:flex-row') ||
    strip;

  const viewToggle =
    row.querySelector('.facturation-view-mode-toggle, .qawaem-view-toggle') ||
    (row.querySelector('.flex.items-center.gap-0')?.querySelector('button') ? row.querySelector('.flex.items-center.gap-0') : null);
  if (viewToggle) push(viewToggle);

  const toolbar = row.querySelector('.facturation-list-toolbar');
  if (toolbar) {
    Array.from(toolbar.children).forEach((child) => push(child));
  } else {
    const actionCluster = row.querySelector('.flex.items-center.gap-2.flex-wrap');
    if (actionCluster) {
      Array.from(actionCluster.children).forEach((child) => {
        if (child !== viewToggle) push(child);
      });
    }
  }

  const searchInput = row.querySelector('input[type="text"][placeholder]');
  const searchWrap =
    searchInput?.closest('.relative') ||
    searchInput?.parentElement;
  if (searchWrap) push(searchWrap);

  return nodes;
}

function ensureTitleRow(titleCol) {
  const h1 = titleCol.querySelector(':scope > h1, :scope > .page-title-date-row h1');
  if (!h1) return null;

  let row = titleCol.querySelector(':scope > .page-title-date-row');
  if (!row) {
    row = document.createElement('div');
    row.className = 'page-title-date-row';
    if (!safeInsertBefore(h1.parentNode, row, h1)) return null;
    row.appendChild(h1);
  }

  let filtersSlot = row.querySelector('.page-title-date-row__filters, .qawaem-title-filters-slot');
  if (!filtersSlot) {
    filtersSlot = document.createElement('div');
    filtersSlot.className = FILTERS_SLOT_CLASS;
    row.appendChild(filtersSlot);
  }

  return { filtersSlot };
}

function hasBuiltInQawaemFilters(filtersSlot) {
  return Boolean(filtersSlot.querySelector('.qawaem-toolbar-filter, .qawaem-table-toolbar-search'));
}

function normalizeTitleRow(titleCol, filtersSlot) {
  const row = titleCol.querySelector(':scope > .page-title-date-row');
  if (!row) return;

  let titleWrap = row.querySelector('.page-title-date-row__title');
  const h1 = row.querySelector('h1');
  if (!titleWrap && h1) {
    titleWrap = document.createElement('div');
    titleWrap.className = 'page-title-date-row__title shrink-0 flex items-center gap-3 flex-wrap';
    if (!safeInsertBefore(row, titleWrap, h1)) return;
    titleWrap.appendChild(h1);
  }

  Array.from(row.children).forEach((child) => {
    if (child === titleWrap || child === filtersSlot) return;
    if (!filtersSlot.contains(child)) {
      rememberHoistOrigin(child);
      child.classList.add('qawaem-hoisted-control');
      filtersSlot.appendChild(child);
    }
  });
}

function shouldSkipToolbarHoist(pathname, container) {
  if (SKIP_HOIST_PATHS.has(pathname)) return true;
  if (!container) return true;
  if (container.classList.contains('archives-container')) return true;
  if (container.dataset.qawaemSkipToolbarHoist === 'true') return true;
  return false;
}

function restoreToolbarDom(container, { unwrapTitleRow = true } = {}) {
  restoreHoistedNodes(container);
  if (unwrapTitleRow) {
    restoreTitleRowDom(container);
  }
}

function runHoist(pathname) {
  const container = document.querySelector('.console-main-scroll .employees-container');
  if (!container || shouldSkipToolbarHoist(pathname, container)) return;

  const toolbarLayout = container.querySelector('.list-toolbar-mobile-layout');
  const titleCol = toolbarLayout?.querySelector(':scope > div:first-child');
  if (!titleCol) return;

  const titleRow = ensureTitleRow(titleCol);
  if (!titleRow) return;

  const { filtersSlot } = titleRow;
  normalizeTitleRow(titleCol, filtersSlot);

  if (hasBuiltInQawaemFilters(filtersSlot)) return;

  const strip = findControlStrip(container);
  if (!strip) return;

  const nodes = collectHoistNodes(strip);
  if (!nodes.length) return;

  let moved = false;
  nodes.forEach((node) => {
    if (!filtersSlot.contains(node)) {
      rememberHoistOrigin(node);
      node.classList.add('qawaem-hoisted-control');
      filtersSlot.appendChild(node);
      moved = true;
    }
  });

  if (moved) {
    strip.classList.add('qawaem-toolbar-source--hoisted');
    strip.dataset.qawaemHoistSource = 'done';
  }
}

/**
 * Moves search, view toggle, and table filters from the table strip into the page title row (Console layout).
 */
export default function QawaemListToolbarHoist() {
  const location = useLocation();

  useLayoutEffect(() => {
    const pathname = location.pathname;
    if (HIDE_PATHS.has(pathname)) return undefined;

    const container = document.querySelector('.console-main-scroll .employees-container');

    if (shouldSkipToolbarHoist(pathname, container)) {
      restoreHoistedNodes(container);
      return undefined;
    }

    runHoist(pathname);

    let retryFrame = 0;
    const retryTimer = window.setTimeout(() => {
      retryFrame = requestAnimationFrame(() => runHoist(pathname));
    }, 150);

    return () => {
      window.clearTimeout(retryTimer);
      cancelAnimationFrame(retryFrame);
      restoreToolbarDom(container, {
        unwrapTitleRow: !shouldSkipToolbarHoist(pathname, container),
      });
    };
  }, [location.pathname]);

  return null;
}
